import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Copy, Check } from 'lucide-react';
import { NETWORKS, DEFAULT_KEY } from '../config/networks';

// The environment's primary network (first exposed / overlay defaultNetwork).
const PRIMARY = NETWORKS[DEFAULT_KEY];

// Contract Address Card Component
interface ContractAddressCardProps {
  networkName: string;
  networkSymbol: string;
  contractAddress: string;
  explorerUrl: string;
  bgColor: string;
  iconBg: string;
  textColor: string;
}

function ContractAddressCard({
  networkName,
  networkSymbol,
  contractAddress,
  explorerUrl,
  bgColor,
  iconBg,
  textColor
}: ContractAddressCardProps) {
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(contractAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <div className={`${bgColor} rounded-xl p-6 border border-dark-600 hover:border-dark-500 hover:shadow-lg transition-shadow duration-300`}>
      <div className="flex items-center mb-4">
        <div className={`w-12 h-12 ${iconBg} rounded-lg flex items-center justify-center mr-4`}>
          <span className="text-white font-bold text-lg">{networkSymbol}</span>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-dark-100">{networkName}</h3>
          <p className="text-sm text-dark-400">wDingocoin Contract</p>
        </div>
      </div>

      {/* Contract Address */}
      <div className="mb-4">
        <label className="text-sm font-medium text-dark-300 mb-2 block">Contract Address</label>
        <div className="bg-dark-900/50 rounded-lg p-3 border border-dark-600">
          <div className="flex items-center justify-between">
            <code className="text-sm font-mono text-dark-200 break-all flex-1 mr-2">
              {contractAddress}
            </code>
            <button
              onClick={copyToClipboard}
              className={`flex-shrink-0 p-2 rounded-md transition-colors duration-200 ${
                copied 
                  ? 'bg-green-100 text-green-600' 
                  : `hover:${bgColor} ${textColor} hover:text-opacity-80`
              }`}
              title="Copy to clipboard"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Explorer Link */}
      <a
        href={`${explorerUrl}/address/${contractAddress}`}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center ${textColor} hover:text-opacity-80 transition-colors duration-200 text-sm font-medium`}
      >
        View on Explorer
        <ArrowRight className="w-4 h-4 ml-1" />
      </a>
    </div>
  );
}

export default function HomePage() {
  // Minimal landing: hero + ecosystem + CTA

  return (
    <div className="min-h-screen bg-gradient-to-b from-dark-900 via-dark-800 to-dark-800">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 lg:py-24">
          <div className="text-center animate-fade-in">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 sm:mb-6 animate-slide-up leading-tight">
              <span className="text-gradient">wDingocoin</span>{' '}
              <span className="text-dark-100">Bridge</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-dark-400 mb-6 sm:mb-8 max-w-4xl mx-auto animate-slide-up leading-relaxed px-2">
              Seamlessly convert between <span className="font-semibold text-primary-600">Dingocoin</span> and{' '}
              <span className="font-semibold text-secondary-600">wDingocoin</span> across multiple blockchains
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center animate-bounce-in max-w-md sm:max-w-none mx-auto">
              <Link
                to="/bridge"
                className="btn-primary group inline-flex items-center space-x-2 text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 w-full sm:w-auto justify-center"
              >
                <span>Start Bridging</span>
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/status"
                className="btn-outline group inline-flex items-center space-x-2 text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 w-full sm:w-auto justify-center"
              >
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>View Statistics</span>
              </Link>
            </div>
          </div>

          {/* Floating Elements - Hidden on mobile, responsive positioning */}
          <div className="hidden md:block absolute top-20 left-4 lg:left-10 w-16 h-16 lg:w-20 lg:h-20 bg-primary-200 rounded-full opacity-20 animate-float"></div>
          <div className="hidden lg:block absolute top-40 right-12 xl:right-20 w-12 h-12 lg:w-16 lg:h-16 bg-secondary-200 rounded-full opacity-30 animate-float" style={{animationDelay: '1s'}}></div>
          <div className="hidden md:block absolute bottom-20 left-1/4 w-10 h-10 lg:w-12 lg:h-12 bg-green-200 rounded-full opacity-25 animate-float" style={{animationDelay: '2s'}}></div>
        </div>
      </div>


      {/* Network Support Section */}
      <div className="py-12 sm:py-16 lg:py-20 bg-dark-900/80 border-y border-dark-700/80 text-dark-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 animate-slide-up px-2">
            <span className="text-gradient">LitVM</span> Testnet
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-8 sm:mb-12 max-w-2xl mx-auto animate-slide-up px-4">
            Bridge your Dingocoin to LitVM — Litecoin's EVM layer — with valueless testnet coins
          </p>

          <div className="grid grid-cols-1 gap-6 sm:gap-8 animate-slide-up max-w-sm mx-auto">
            <div className="group p-4 sm:p-6 rounded-xl hover:bg-dark-800 transition-colors duration-300">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-3 sm:mb-4 bg-sky-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <span className="text-sm sm:text-lg font-bold text-white">zkLTC</span>
              </div>
              <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">LitVM Testnet</h3>
              <p className="text-gray-400 text-xs sm:text-sm">Litecoin L2 · chain ID 4441</p>
            </div>
          </div>
        </div>
      </div>

      {/* Contract Addresses Section */}
      <div className="py-12 sm:py-16 lg:py-20 bg-dark-800/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 animate-slide-up">
              <span className="text-gradient">Contract</span>{' '}
              <span className="text-dark-100">Addresses</span>
            </h2>
            <p className="text-lg sm:text-xl text-dark-400 mb-8 max-w-2xl mx-auto animate-slide-up">
              Verified wDingocoin smart contract address on LitVM testnet
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:gap-8 max-w-xl mx-auto">
            {/* LitVM Contract */}
            <ContractAddressCard
              networkName={PRIMARY.name}
              networkSymbol={PRIMARY.nativeCurrency.symbol}
              contractAddress={PRIMARY.contractAddress}
              explorerUrl={PRIMARY.explorerUrl}
              bgColor="bg-sky-950/25"
              iconBg="bg-sky-500"
              textColor="text-sky-400"
            />
          </div>

          <div className="text-center mt-8 sm:mt-12">
            <p className="text-sm text-dark-500 max-w-2xl mx-auto">
              Always verify contract addresses on the official blockchain explorers before interacting
            </p>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="py-12 sm:py-16 lg:py-20 bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-slide-up">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 px-2">
            Ready to Bridge Your Dingocoin?
          </h2>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-md sm:max-w-none mx-auto">
            <Link
              to="/bridge"
              className="bg-white text-primary-600 hover:bg-gray-100 font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg w-full sm:w-auto"
            >
              Launch Bridge App
            </Link>
            <Link
              to="/status"
              className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-lg transition-all duration-300 hover:scale-105 w-full sm:w-auto"
            >
              Check Network Status
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}